import type { RuleSet } from '@kkp-fraud/shared';
import type { Proposal, ProposalDiff, ProposalFieldChange, ProposalRuleDiff, ReplaySummary } from '../proposals';
import type { DemoVersion } from './versions';

/**
 * Three settled proposals — two published, one rejected — that sit behind the real ones in
 * the proposal list and the Log. They are history: the proposal screen renders no action
 * buttons for them (`isDemoProposalId`), and `get` answers them locally.
 *
 * The two published ones ARE the demo releases 0.9.2 and 0.9.3: same comment, same
 * documents, `publishedUtc` equal to the release time. Their diffs are computed the way the
 * admin service computes them (`RuleSetDiff.cs`: rule-level, top-level keys).
 */

const ANALYST = 'fraud.analyst@kkp.co.th (u-fa01)';
const LEAD = 'fraud.lead@kkp.co.th (u-fl01)';

export const DEMO_PROPOSAL_IDS = {
  pb01: '3f1c8a52-6d0e-4b7a-9c2f-81e5d4a07b19',
  rb01: 'a94e0d17-2b6c-4f38-8e51-5c7d9b3f2e60',
  rb02: 'c27b5e90-8f41-4d1a-b6e3-0a9d4c8f1e72',
} as const;

const IDS = new Set<string>(Object.values(DEMO_PROPOSAL_IDS));

export function isDemoProposalId(id: string): boolean {
  return IDS.has(id);
}

const same = (a: unknown, b: unknown) => JSON.stringify(a ?? null) === JSON.stringify(b ?? null);

type AnyRuleJson = Record<string, unknown> & { id: string; name?: string };

function family(kind: 'ruleBased' | 'patternBased', before: AnyRuleJson[], after: AnyRuleJson[]): ProposalRuleDiff[] {
  const prev = new Map(before.map((r) => [r.id, r]));
  const next = new Map(after.map((r) => [r.id, r]));
  const out: ProposalRuleDiff[] = [];
  for (const [id, rule] of next) {
    const old = prev.get(id);
    if (!old) {
      out.push({ id, name: String(rule.name ?? ''), kind, status: 'added', changes: [] });
      continue;
    }
    const keys = [...new Set([...Object.keys(old), ...Object.keys(rule)])];
    const changes: ProposalFieldChange[] = keys
      // `points` is no change (no scoring since 30 Sep 2026), as in the BFF's RuleSetDiff.
      .filter((k) => k !== 'points' && !same(old[k], rule[k]))
      .map((k) => ({ path: k, before: old[k], after: rule[k] }));
    if (changes.length) out.push({ id, name: String(rule.name ?? ''), kind, status: 'changed', changes });
  }
  for (const [id, rule] of prev) if (!next.has(id)) out.push({ id, name: String(rule.name ?? ''), kind, status: 'removed', changes: [] });
  return out.sort((a, b) => (a.id < b.id ? -1 : a.id > b.id ? 1 : 0));
}

/** The admin service's rule-set diff, for two demo documents. */
export function ruleSetDiff(before: RuleSet, after: RuleSet): ProposalDiff {
  const rules = [
    ...family('ruleBased', before.ruleBased as unknown as AnyRuleJson[], after.ruleBased as unknown as AnyRuleJson[]),
    ...family('patternBased', before.patternBased as unknown as AnyRuleJson[], after.patternBased as unknown as AnyRuleJson[]),
  ];
  const meta: ProposalFieldChange[] = [];
  for (const path of ['failSafe.cardPresentAction', 'failSafe.ecommerceAction', 'failSafe.ecommerceMinAmountThb', 'note']) {
    const dig = (doc: RuleSet) => path.split('.').reduce<unknown>((o, k) => (o as Record<string, unknown> | undefined)?.[k], doc);
    if (!same(dig(before), dig(after))) meta.push({ path, before: dig(before), after: dig(after) });
  }
  const counts = {
    added: rules.filter((r) => r.status === 'added').length,
    removed: rules.filter((r) => r.status === 'removed').length,
    changed: rules.filter((r) => r.status === 'changed').length,
  };
  const dirty = meta.length > 0 || rules.length > 0;
  const bits: string[] = [];
  if (counts.added) bits.push(`${counts.added} added`);
  if (counts.removed) bits.push(`${counts.removed} removed`);
  if (counts.changed) bits.push(`${counts.changed} changed`);
  if (meta.length) bits.push(`${meta.length} setting${meta.length === 1 ? '' : 's'}`);
  return { meta, rules, counts, dirty, summary: dirty ? bits.join(' · ') : 'no changes' };
}

function replay(
  changed: { from: string; to: string; rows: number }[],
  newly: { ruleId: string; rows: number }[],
  labels: { caught: [number, number]; flagged: [number, number] },
): ReplaySummary {
  const fraud = 64;
  const genuine = 936;
  return {
    corpus: { path: 'events.ndjson', totalInCorpus: 4812, cap: 1000, capped: true, rowsAttempted: 1000, rowsCompared: 1000, rowsFailed: 0 },
    failures: [],
    decisionsChanged: { rows: changed.reduce((a, c) => a + c.rows, 0), byTransition: changed },
    rulesNewlyFiring: newly,
    rulesNoLongerFiring: [],
    labelTable: {
      labelSource: 'simulated labels',
      note: 'labelledFraud comes from the scenario generator, not from confirmed fraud cases.',
      baseline: {
        labelledFraud: fraud,
        caught: labels.caught[0],
        missed: fraud - labels.caught[0],
        labelledGenuine: genuine,
        genuineFlagged: labels.flagged[0],
        genuineClean: genuine - labels.flagged[0],
      },
      candidate: {
        labelledFraud: fraud,
        caught: labels.caught[1],
        missed: fraud - labels.caught[1],
        labelledGenuine: genuine,
        genuineFlagged: labels.flagged[1],
        genuineClean: genuine - labels.flagged[1],
      },
    },
  };
}

const iso = (ms: number) => new Date(ms).toISOString();
const MIN = 60_000;
const HOUR = 60 * MIN;

/**
 * Newest first. `versions` is `demoVersions(...)` (newest first: 0.9.3, 0.9.2, 0.9.1, 0.9.0).
 * Returns [] when the history it describes is not there.
 */
export function demoProposals(versions: readonly DemoVersion[]): Proposal[] {
  const v = (name: string) => versions.find((d) => d.summary.version === name);
  const v093 = v('0.9.3-poc');
  const v092 = v('0.9.2-poc');
  const v091 = v('0.9.1-poc');
  if (!v093 || !v092 || !v091) return [];
  const t093 = Date.parse(v093.summary.savedUtc);
  const t092 = Date.parse(v092.summary.savedUtc);

  // Rejected: a lower RB-01 score threshold, raised between the two releases.
  const rb01Candidate: RuleSet = structuredClone(v092.detail.ruleSet);
  for (const rule of rb01Candidate.ruleBased) {
    if (rule.id !== 'RB-01') continue;
    for (const list of [rule.clauses, rule.branches?.[0]?.clauses]) {
      for (const c of list ?? []) if (c.field === 'fraud_score' && c.op === 'gt') c.value = 600;
    }
  }
  const tRejected = t092 + 2 * 24 * HOUR + 3 * HOUR + 12 * MIN;

  return [
    {
      id: DEMO_PROPOSAL_IDS.pb01,
      status: 'published',
      createdBy: ANALYST,
      createdUtc: iso(t093 - 26 * HOUR - 14 * MIN),
      comment: v093.summary.comment,
      baseVersion: '0.9.2-poc',
      approvedBy: LEAD,
      approvedUtc: iso(t093 - 18 * MIN),
      publishedVersion: '0.9.3-poc',
      publishedUtc: v093.summary.savedUtc,
      diff: ruleSetDiff(v092.detail.ruleSet, v093.detail.ruleSet),
      validation: { ok: true, errors: [], warnings: [], unconfirmedThresholds: [] },
      replay: replay([{ from: 'APPROVE', to: 'STEP_UP', rows: 4 }], [{ ruleId: 'PB-01', rows: 4 }], {
        caught: [49, 52],
        flagged: [11, 12],
      }),
      ruleSet: v093.detail.ruleSet,
    },
    {
      id: DEMO_PROPOSAL_IDS.rb01,
      status: 'rejected',
      createdBy: ANALYST,
      createdUtc: iso(tRejected),
      comment: 'RB-01 fraud score threshold 700 → 600',
      baseVersion: '0.9.2-poc',
      diff: ruleSetDiff(v092.detail.ruleSet, rb01Candidate),
      validation: { ok: true, errors: [], warnings: [], unconfirmedThresholds: [] },
      replay: replay(
        [
          { from: 'APPROVE', to: 'DECLINE', rows: 23 },
          { from: 'STEP_UP', to: 'DECLINE', rows: 2 },
        ],
        [{ ruleId: 'RB-01', rows: 25 }],
        { caught: [49, 51], flagged: [11, 34] },
      ),
      ruleSet: rb01Candidate,
    },
    {
      id: DEMO_PROPOSAL_IDS.rb02,
      status: 'published',
      createdBy: ANALYST,
      createdUtc: iso(t092 - 5 * HOUR - 41 * MIN),
      comment: v092.summary.comment,
      baseVersion: '0.9.1-poc',
      approvedBy: LEAD,
      approvedUtc: iso(t092 - 25 * MIN),
      publishedVersion: '0.9.2-poc',
      publishedUtc: v092.summary.savedUtc,
      diff: ruleSetDiff(v091.detail.ruleSet, v092.detail.ruleSet),
      validation: { ok: true, errors: [], warnings: [], unconfirmedThresholds: [] },
      replay: replay([{ from: 'APPROVE', to: 'STEP_UP', rows: 3 }], [{ ruleId: 'RB-02', rows: 3 }], {
        caught: [46, 49],
        flagged: [11, 11],
      }),
      ruleSet: v092.detail.ruleSet,
    },
  ];
}
